/**
 * Shared drawing utilities used by both the live animation loop
 * and the offline export renderer.
 */

/**
 * Draw a tiling panorama that fills the canvas height and scrolls horizontally.
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement} image
 * @param {number} offset        current pixel offset
 * @param {number} direction      1 = scroll right, -1 = scroll left
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @param {number} motionBlur     blur strength in pixels (0 = off)
 */
export function drawPanorama(ctx, image, offset, direction, canvasWidth, canvasHeight, motionBlur = 0) {
  if (!image || !ctx) return

  const scale = canvasHeight / image.height
  const scaledWidth = image.width * scale

  if (motionBlur > 0) {
    // Multi-sample directional motion blur
    const samples = Math.min(Math.ceil(motionBlur) + 1, 20)
    const spread = motionBlur // pixel spread
    ctx.save()
    ctx.globalAlpha = 1 / samples

    for (let s = 0; s < samples; s++) {
      const sampleOffset = offset + (s / (samples - 1) - 0.5) * spread
      drawPanoramaTile(ctx, image, sampleOffset, direction, scaledWidth, canvasWidth, canvasHeight)
    }

    ctx.restore()
  } else {
    drawPanoramaTile(ctx, image, offset, direction, scaledWidth, canvasWidth, canvasHeight)
  }
}

function drawPanoramaTile(ctx, image, offset, direction, scaledWidth, canvasWidth, canvasHeight) {
  let x = -(offset * direction) % scaledWidth
  // Normalize x into [-scaledWidth, 0]
  while (x > 0) x -= scaledWidth
  while (x < -scaledWidth) x += scaledWidth

  for (let drawX = x; drawX < canvasWidth; drawX += scaledWidth) {
    ctx.drawImage(image, drawX, 0, scaledWidth, canvasHeight)
  }
}

/**
 * Parse a hex colour string and return {r, g, b}.
 */
function hexToRgb(hex) {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.substring(0, 2), 16) || 0,
    g: parseInt(h.substring(2, 4), 16) || 0,
    b: parseInt(h.substring(4, 6), 16) || 0,
  }
}

/**
 * Draw a stamp (with optional frame and shadow) onto a canvas.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} stamp           stamp config object
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 */
export function drawStamp(ctx, stamp, canvasWidth, canvasHeight) {
  if (!stamp.image || !stamp.visible) return

  const x = (stamp.x / 100) * canvasWidth
  const y = (stamp.y / 100) * canvasHeight

  // Base size relative to canvas height only (avoids deformation on aspect change)
  const dim = canvasHeight * 0.18 * stamp.scale
  const ratio = stamp.aspectRatio || 1
  const w = ratio >= 1 ? dim * ratio : dim
  const h = ratio >= 1 ? dim : dim / ratio

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate((stamp.rotation * Math.PI) / 180)

  // Shadow
  if (stamp.shadowEnabled) {
    const opacity = stamp.shadowOpacity ?? 0.45
    const { r, g, b } = hexToRgb(stamp.shadowColor || '#000000')
    ctx.shadowColor = `rgba(${r},${g},${b},${opacity})`
    ctx.shadowBlur = stamp.shadowBlur ?? 12
    ctx.shadowOffsetX = stamp.shadowOffsetX ?? 3
    ctx.shadowOffsetY = stamp.shadowOffsetY ?? 3
  }

  if (stamp.frameType === 'perforated') {
    drawStampPerforated(ctx, stamp.image, -w / 2, -h / 2, w, h, stamp)
  } else if (stamp.frameType === 'rounded') {
    drawStampRounded(ctx, stamp.image, -w / 2, -h / 2, w, h, stamp)
  } else {
    ctx.drawImage(stamp.image, -w / 2, -h / 2, w, h)
  }

  ctx.restore()
}

export function drawStampPerforated(ctx, image, x, y, width, height, stamp) {
  const perfSize = 4
  const perfSpacing = 8
  const borderWidth = stamp.borderWidth ?? 8

  ctx.save()
  ctx.beginPath()

  // Top edge
  ctx.moveTo(x, y)
  for (let i = 0; i < width; i += perfSpacing) {
    ctx.arc(x + i + perfSpacing / 2, y, perfSize / 2, Math.PI, 0, false)
  }
  ctx.lineTo(x + width, y)

  // Right edge
  for (let i = 0; i < height; i += perfSpacing) {
    ctx.arc(x + width, y + i + perfSpacing / 2, perfSize / 2, -Math.PI / 2, Math.PI / 2, false)
  }
  ctx.lineTo(x + width, y + height)

  // Bottom edge
  for (let i = width; i > 0; i -= perfSpacing) {
    ctx.arc(x + i - perfSpacing / 2, y + height, perfSize / 2, 0, Math.PI, false)
  }
  ctx.lineTo(x, y + height)

  // Left edge
  for (let i = height; i > 0; i -= perfSpacing) {
    ctx.arc(x, y + i - perfSpacing / 2, perfSize / 2, Math.PI / 2, -Math.PI / 2, false)
  }

  ctx.closePath()
  ctx.fillStyle = stamp.frameColor || '#ffffff'
  ctx.fill()

  // Turn off shadow for the inner image draw
  ctx.shadowColor = 'transparent'

  ctx.drawImage(
    image,
    x + borderWidth,
    y + borderWidth,
    width - borderWidth * 2,
    height - borderWidth * 2
  )

  ctx.restore()
}

export function drawStampRounded(ctx, image, x, y, width, height, stamp) {
  const borderWidth = stamp.borderWidth ?? 6
  const radius = stamp.cornerRadius ?? 8

  ctx.save()

  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
  ctx.fillStyle = stamp.frameColor || '#ffffff'
  ctx.fill()

  ctx.shadowColor = 'transparent'

  ctx.beginPath()
  ctx.roundRect(
    x + borderWidth,
    y + borderWidth,
    width - borderWidth * 2,
    height - borderWidth * 2,
    Math.max(0, radius - borderWidth / 2)
  )
  ctx.clip()

  ctx.drawImage(
    image,
    x + borderWidth,
    y + borderWidth,
    width - borderWidth * 2,
    height - borderWidth * 2
  )

  ctx.restore()
}
